import { useEffect, useRef } from "react";

const shortcutKey = "/";
const typingTags = ["INPUT", "TEXTAREA", "SELECT"];
const typingRoles = ["textbox", "searchbox", "combobox"];
const openDialogSelector = "[role=\"dialog\"], [role=\"alertdialog\"]";

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  const role = target.getAttribute("role") ?? "";

  return typingTags.includes(target.tagName) || typingRoles.includes(role);
};

// A dialog keeps the focus inside it until it closes (SHELL-R53).
const isDialogOpen = (): boolean => document.querySelector(openDialogSelector) !== null;

const isShortcut = (event: KeyboardEvent): boolean =>
  event.key === shortcutKey &&
  !event.isComposing &&
  !event.ctrlKey &&
  !event.altKey &&
  !event.metaKey &&
  !event.defaultPrevented &&
  !isTypingTarget(event.target) &&
  !isDialogOpen();

// Runs `onShortcut` when the admin presses "/" alone while not typing in a field and with no
// dialog open, and keeps the "/" from being typed (SHELL-R49, SHELL-R51, SHELL-R53).
export const useSearchShortcut = (onShortcut: () => void) => {
  const onShortcutRef = useRef(onShortcut);
  onShortcutRef.current = onShortcut;

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isShortcut(event)) return;

      event.preventDefault();
      onShortcutRef.current();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);
};
